import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { setSelectedDeclaration } from '../../store/reducers';
import { useLanguage, getDeclarationManagementTranslation, getTranslation } from '../../hooks/useLanguage';
import { readDuasDeclarations, duasKey } from '../../utils/declarationKindUtils';

export const DeclarationsTable = ({ exportaciones, isLoading, handleEdit, openDeleteModal }) => {
    const [openDropdownId, setOpenDropdownId] = useState(null);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [isSpanish] = useLanguage();
    const groupedTaricJsonOutputs = useSelector((state) => state.groupedTaricJsonOutputs || []);

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString(isSpanish ? 'es-ES' : 'en-GB', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        });
    };

    const renderKindBadge = (v) => {
        const isImport = v === 'import';
        return (
            <span
                className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${
                    isImport
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-emerald-100 text-emerald-700'
                }`}
            >
                {getTranslation(isImport ? 'import' : 'export', isSpanish)}
            </span>
        );
    };

    const dataFields = [
        { key: 'expediente', label: getDeclarationManagementTranslation('expediente', isSpanish) },
        { key: 'kind', label: getTranslation('declarationType', isSpanish), format: renderKindBadge },
        { key: 'date_created', label: getDeclarationManagementTranslation('fechaCreacion', isSpanish) || (isSpanish ? 'Fecha Creación' : 'Date Created'), format: formatDate },
        { key: 'partidas_count', label: isSpanish ? 'Número de Partidas' : 'Number of Items' },
        { key: 'pais_exportacion', label: isSpanish ? 'País Exportación' : 'Export Country' },
        { key: 'pais_destino', label: getDeclarationManagementTranslation('paisDestino', isSpanish) },
    ];

    const handleView = (exportacion) => {
        // Store the declaration data in selectedDeclaration. The taric object may
        // carry either duas.exportaciones or duas.importaciones; resolve either.
        const { declarations } = readDuasDeclarations(exportacion.exportacionData);
        const exportacionData = declarations?.[0] || {};
        dispatch(setSelectedDeclaration({
            id: exportacion.id,
            ocr_id: exportacion.ocr_id,
            data: [exportacionData],
            files: [] // Files might not be available in taric_json_output
        }));

        setTimeout(() => {
            navigate(`/declaration/${exportacion.id}`);
        }, 0);
    };

    const handleExportToTaric = (exportacion) => {
        // Extract taricJson from the exportacion - use API data directly New structure: exportacion.exportacion contains {id, taric, date_created} exportacion.exportacionData is the taric object
        const exportacionObj = exportacion.exportacion;
        const taricJson = exportacionObj?.taric || exportacion.exportacionData || {};
        
        // Find the index of this declaration within its group's kind array.
        const matchingGroup = groupedTaricJsonOutputs.find(
            group => String(group.ocr_id) === String(exportacion.ocr_id)
        );
        const kindArray = matchingGroup?.[duasKey(exportacion.kind)];
        const selectedIndex = kindArray?.findIndex(
            exp => exp.id === exportacion.id
        ) ?? 0;
        
        // Store in Redux preparedJson with selected index
        dispatch({ 
            type: 'SET_TARIC_JSON_FROM_GROUPED', 
            payload: {
                taricJson,
                ocr_input_id: exportacion.ocr_id?.toString(),
                exportacionesIndex: selectedIndex
            }
        });
        
        // Open the generate tab
        window.dispatchEvent(new CustomEvent('openGenerateTab'));
    };


    return (
        <table className="min-w-full border-collapse border border-gray-300 mt-4 text-sm">
            <thead>
                <tr>
                    <th className="border border-gray-300 p-2">{getDeclarationManagementTranslation('id', isSpanish)}</th>
                    {dataFields.map(field => (
                        <th key={field.key} className="border border-gray-300 p-2">{field.label}</th>
                    ))}
                    <th className="border border-gray-300 p-2">{getDeclarationManagementTranslation('actions', isSpanish)}</th>
                </tr>
            </thead>
            <tbody>
                {exportaciones && exportaciones.length > 0 ? (
                    exportaciones.map((exportacion) => {
                        const dropdownKey = `${exportacion.id}`;
                        const isOpen = openDropdownId === dropdownKey;
                        
                        return (
                            <tr key={exportacion.id}>
                                <td className="border border-gray-300 p-2">{exportacion.id}</td>
                                {dataFields.map(field => {
                                    const value = exportacion[field.key];
                                    return (
                                        <td key={field.key} className="border border-gray-300 p-2">
                                            {field.format ? field.format(value) : (value !== undefined && value !== null ? value : '-')}
                                        </td>
                                    );
                                })}
                                <td className="border border-gray-300 p-2 w-fit">
                                    <div className='relative text-sm'>
                                        <button 
                                            className={`flex gap-2 justify-between items-center border-containera bg-gray-100 hover:bg-gray-200 rounded-full p-2 px-4 w-full`} 
                                            onClick={() => setOpenDropdownId(isOpen ? null : dropdownKey)}
                                        >
                                            <p className='truncate'>{getDeclarationManagementTranslation('selectAction', isSpanish)}</p>
                                            <ChevronDown className={`w-4 h-4 ${isOpen && "rotate-180"}`} />
                                        </button>
                                        {isOpen && (
                                            <div className='min-w-52 right-0 flex flex-col absolute z-10 bg-white mt-1 w-full border-containera shadow-md rounded-xl max-h-80 overflow-auto p-2'>
                                                <p className='opacity-75 px-2 py-1 text-xs'>{getDeclarationManagementTranslation('selectAction', isSpanish)}</p>
                                                <span 
                                                    className="px-2 py-1 hover:bg-gray-100 cursor-pointer rounded-full"
                                                    onClick={() => handleView(exportacion)}
                                                >
                                                    {getDeclarationManagementTranslation('view', isSpanish)}
                                                </span>
                                                <span
                                                    className='px-2 py-1 hover:bg-gray-100 cursor-pointer rounded-full'
                                                    onClick={() => handleExportToTaric(exportacion)}
                                                >
                                                    {getDeclarationManagementTranslation(
                                                        exportacion.kind === 'import' ? 'importToTaric' : 'exportToTaric',
                                                        isSpanish
                                                    )}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        );
                    })
                ) : (
                    <tr>
                        <td colSpan={dataFields.length + 2} className="border border-gray-300 p-2 text-center">
                            {isLoading ? (isSpanish ? 'Cargando...' : 'Loading...') : getDeclarationManagementTranslation('noDeclarationsFound', isSpanish)}
                        </td>
                    </tr>
                )}
            </tbody>
        </table>
    )
}